<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    :id="id"
    ref="dropdownContainer"
    class="drop-down"
    :class="{
      'alternate-style': useAlternateStyle,
      'table-filter-style': useTableFilterStyle,
      'multiselect-style': useMultiselectStyle,
      disabled: isDisabled,
    }"
    :style="`border-radius: ${radiusLeft} ${radiusRight} ${radiusRight} ${radiusLeft};`"
    @keyup.esc="toggleDropDown"
    @keyup.arrow-up="onUpKey"
    @keyup.arrow-down="onDownKey">
    <button
      id="main-button"
      ref="mainButton"
      v-tooltip.bottom="{
        content: buttonTooltipContent,
        delay: 500,
        visible: showTooltipOnButton && !isDropDownPopupVisible,
      }"
      class="btn-drop-down btn btn-rounded"
      :class="buttonClasses"
      :style="`border-radius: ${radiusLeft} ${radiusRight} ${radiusRight} ${radiusLeft};`"
      @click.prevent="toggleDropDown">
      <template v-if="icon">
        <div class="icon ml-1">
          <component
            :is="icon"
            :style="iconStyle" />
        </div>
      </template>
      <div
        v-if="isReadOnly"
        v-tooltip="capitalize($t('states.readOnly'))"
        class="icon-read-only">
        <icon-lock />
      </div>
      <div
        v-if="
          hasSelection &&
          !isMultiselect &&
          useImage &&
          selectedOptions?.length > 0 &&
          selectedOptions[0][optionImageProperty]
        "
        class="option-image">
        <img
          v-if="
            selectedOptions[0][optionImageProperty].startsWith('http') ||
            selectedOptions[0][optionImageProperty].startsWith('www') ||
            selectedOptions[0][optionImageProperty].startsWith('/')
          "
          :src="selectedOptions[0][optionImageProperty]"
          alt=""
          :style="optionImageStyle" />
        <div
          v-else
          v-sanitize-html="selectedOptions[0][optionImageProperty]"
          class="svg-container"
          :style="optionImageStyle" />
      </div>
      <span
        v-if="isDisabled"
        class="d-flex mr-2">
        <IconLocked />
      </span>
      <span
        v-sanitize-html="labelOrValue"
        class="label" />
      <button
        v-if="(!hasSelection || !showClear || !isMultiselect) && showArrow"
        class="btn blank"
        @click.stop.prevent="toggleDropDown">
        <icon-arrow-down />
      </button>
      <button
        v-else-if="!isReadOnly && showArrow"
        class="btn blank clear-btn"
        @click.stop.prevent="clear">
        <icon-close />
      </button>
      <slot />
      <div
        v-if="showFloatingLabel && (selectedOptions.length > 0 || labelPlaceholder != null)"
        class="drop-down-floating-label badge-default">
        {{ shortLabel || label }}
      </div>
      <div
        v-if="isMandatory && !hasSelection"
        v-tooltip="capitalize($t('messages.selectAtLeastOneItem'))"
        class="mandatory" />
    </button>
    <div
      ref="popupContainer"
      class="drop-down-popup-container"
      :class="{
        visible: isDropDownPopupVisible,
        'allow-exclusions': allowExclusions,
        'align-right': alignDropDownToRight,
        readonly: isReadOnly,
        'open-upwards': openUpwards,
        'single-select': !isMultiselect,
      }"
      :style="`width: ${dropDownContainerWidth};min-width: ${dropDownContainerMinWidth};${popupPositionFixed ? 'position: fixed;' : ''}`">
      <div
        v-if="showPopupHeader"
        class="drop-down-popup-header">
        <template v-if="icon">
          <div class="icon">
            <component :is="icon" />
          </div>
        </template>
        <span class="label">{{ popupLabel != null ? popupLabel : label }}</span>
        <button
          name="closeDropDown"
          @click.stop.prevent="closeDropDown">
          <icon-close />
        </button>
      </div>
      <slot name="drop-down-popup-top" />
      <!-- SELECT ALL TOGGLE -->
      <div
        v-if="!isReadOnly && !isAutoComplete && options != null && options.length > 1 && showSelectAll"
        class="drop-down-popup-toggle">
        <label class="flex-h">
          <span class="label">{{ capitalize($t('actions.selectAll')) }}</span>
          <toggle-button
            :color="colors.success"
            :sync="true"
            :value="shouldSelectAll"
            @click.stop.prevent=""
            @change="(val) => handleSelectAllToggleButtonChange(val)" />
        </label>
      </div>

      <!-- SEARCH INPUT BOX -->
      <div
        v-if="!isReadOnly && (isAutoComplete || enableSearch || options?.length >= 10)"
        class="drop-down-popup-options">
        <input
          ref="searchInput"
          v-model="searchTerm"
          class="form-control search"
          type="text"
          :placeholder="capitalize(searchPlaceholder)"
          :style="{ marginBottom: '0 !important' }" />
      </div>

      <!-- OPTIONS -->
      <div
        v-if="(isReadOnly && selectedOptions.length > 0) || !isReadOnly"
        class="drop-down-popup-options"
        :class="{ 'single-select': !isMultiselect }">
        <!-- LOADER -->
        <div
          v-if="isLoading"
          class="m-3">
          <loader-small />
          {{ capitalize($t('states.loading')) }}...
        </div>

        <!-- OPTIONS LIST -->
        <div v-else>
          <slot name="drop-down-popup-search-results" />
          <div
            v-if="visibleFilteredOptions.length > 1000"
            class="ml-3 mr-3">
            <i>{{ $t('messages.tooManyItemsToDisplay') }}</i>
          </div>
          <div
            v-else-if="visibleFilteredOptions.length == 0 && !isAutoComplete"
            class="ml-3 mr-3">
            <slot
              v-if="$slots?.noResult"
              name="noResult" />
            <i v-else>{{ capitalize($t('messages.noResultsFound')) }}</i>
          </div>
          <ul v-else>
            <!-- USE GROUPS -->
            <template v-if="useGroups">
              <div
                v-for="(group, i) in visibleFilteredOptions"
                :key="i">
                <div v-if="canToggleGroups">
                  <li
                    :name="group[optionLabelProperty]"
                    :class="{ disabled: optionDisabledProperty != null && group[optionDisabledProperty] }">
                    <!-- USE CHECKBOXES -->
                    <template v-if="useCheckboxes">
                      <label
                        class="flex-h"
                        :class="{
                          checked: internalSelectedOptions.some(
                            (x) => x[optionTrackByProperty] === group[optionTrackByProperty],
                          ),
                        }">
                        <check-box
                          v-if="isMultiselect"
                          :checked="isSelected(group)"
                          :is-read-only="isReadOnly"
                          :use-for-exclusion="isExcluded(group)"
                          @click.stop.prevent="toggleOption(group)" />
                        <span
                          class="group-label"
                          :title="group[optionLabelProperty]"
                          >{{ group[optionLabelProperty] }}
                          <span
                            v-if="showTooltipOnOptions && group[optionLabelDescription] != null"
                            v-tooltip.top="group[optionLabelDescription]"
                            class="label-tooltip">
                            <icon-info />
                          </span>
                        </span>
                      </label>
                      <a
                        v-if="allowExclusions && !isExcluded(group)"
                        class="option-action visible-on-hover mr-2"
                        data-action-type="exclude"
                        @click.stop.prevent="toggleExclusion(group)"
                        >{{ $t('actions.exclude') }}</a
                      >
                      <a
                        v-if="allowExclusions && isExcluded(group)"
                        class="option-action visible-on-hover mr-2"
                        data-action-type="include"
                        @click.stop.prevent="toggleExclusion(group)"
                        >{{ $t('actions.include') }}</a
                      >
                      <a
                        v-if="isMultiselect"
                        class="option-action visible-on-hover"
                        data-action-type="only"
                        @click.stop.prevent="selectOnly(group)"
                        >{{ $t('messages.only') }}</a
                      >
                    </template>
                  </li>
                </div>
                <div
                  v-else
                  class="group-label">
                  {{ group.name }}
                </div>
                <ul
                  class="group-options"
                  :class="{ 'ml-3': canToggleGroups }">
                  <li
                    v-for="(option, i) in filteredGroupResult(group)"
                    :key="i"
                    :name="option[optionLabelProperty]"
                    :class="{ disabled: optionDisabledProperty != null && option[optionDisabledProperty] }">
                    <template v-if="useCheckboxes">
                      <label
                        class="flex-h"
                        @click.stop.prevent="toggleOption(option)">
                        <check-box
                          v-if="isMultiselect"
                          :checked="
                            internalSelectedOptions.some(
                              (x) => x[optionTrackByProperty] === option[optionTrackByProperty],
                            )
                          "
                          :use-for-exclusion="
                            internalExcludedOptions.some(
                              (x) => x[optionTrackByProperty] === option[optionTrackByProperty],
                            )
                          "
                          :is-read-only="isReadOnly"
                          @click.stop.prevent="toggleOption(option)" />
                        <div
                          v-if="option[optionImageProperty]"
                          class="option-image">
                          <img
                            v-if="
                              option[optionImageProperty].startsWith('http') ||
                              option[optionImageProperty].startsWith('www') ||
                              option[optionImageProperty].startsWith('/')
                            "
                            :src="option[optionImageProperty]"
                            alt=""
                            :style="optionImageStyle" />
                          <div
                            v-else
                            v-sanitize-html="option[optionImageProperty]"
                            class="svg-container"
                            :style="optionImageStyle" />
                        </div>
                        <span
                          class="option-label"
                          :title="option[optionLabelProperty]"
                          >{{ option[optionLabelProperty] }}</span
                        >
                      </label>
                      <a
                        v-if="allowExclusions && !isExcluded(option) && !isReadOnly"
                        class="option-action visible-on-hover mr-2"
                        data-action-type="exclude"
                        @click.stop.prevent="toggleExclusion(option)"
                        >{{ $t('actions.exclude') }}</a
                      >
                      <a
                        v-if="allowExclusions && isExcluded(option) && !isReadOnly"
                        class="option-action visible-on-hover mr-2"
                        data-action-type="include"
                        @click.stop.prevent="toggleExclusion(option)"
                        >{{ $t('actions.include') }}</a
                      >
                      <a
                        v-if="!isReadOnly && isMultiselect"
                        class="option-action visible-on-hover"
                        data-action-type="only"
                        @click.stop.prevent="selectOnly(option)"
                        >{{ $t('messages.only') }}</a
                      >
                    </template>
                    <template v-else>
                      <label
                        class="flex-h"
                        @click="selectOption(option)">
                        <div
                          v-if="option[optionImageProperty]"
                          class="option-image">
                          <img
                            v-if="
                              option[optionImageProperty].startsWith('http') ||
                              option[optionImageProperty].startsWith('www') ||
                              option[optionImageProperty].startsWith('/')
                            "
                            :src="option[optionImageProperty]"
                            alt=""
                            :style="optionImageStyle" />
                          <div
                            v-else
                            v-sanitize-html="option[optionImageProperty]"
                            class="svg-container"
                            :style="optionImageStyle" />
                        </div>
                        <span
                          class="option-label"
                          :class="{ active: option.isDefault }"
                          :title="option[optionLabelProperty]">
                          {{ option[optionLabelProperty] }}
                        </span>
                      </label>
                    </template>
                  </li>
                </ul>
              </div>
            </template>
            <!-- NO GROUPS -->
            <li
              v-for="(option, i) in visibleFilteredOptions"
              v-else
              :key="i"
              :name="option[optionLabelProperty]"
              :class="{
                selected: !isMultiselect && isSelected(option),
                disabled: optionDisabledProperty != null && option[optionDisabledProperty],
              }"
              @click.stop.prevent="
                () => {
                  if (isMultiselect) {
                    toggleOption(option);
                  } else {
                    selectOnly(option);
                    closeDropDown();
                  }
                }
              ">
              <template v-if="isMultiselect">
                <!-- USE CHECKBOXES -->
                <template v-if="useCheckboxes">
                  <label
                    class="flex-h"
                    :class="{
                      checked: internalSelectedOptions.some(
                        (x) => x[optionTrackByProperty] === option[optionTrackByProperty],
                      ),
                    }"
                    @click.stop.prevent="
                      () => {
                        if (isMultiselect) {
                          toggleOption(option);
                        } else {
                          selectOnly(option);
                          closeDropDown();
                        }
                      }
                    ">
                    <check-box
                      v-if="isMultiselect"
                      :checked="isSelected(option)"
                      :is-read-only="isReadOnly"
                      :use-for-exclusion="isExcluded(option)"
                      @click.stop.prevent="toggleOption(option)"
                      @contextmenu.stop.prevent="toggleExclusion(option)" />
                    <div
                      v-if="option[optionImageProperty]"
                      class="option-image">
                      <img
                        v-if="
                          option[optionImageProperty].startsWith('http') ||
                          option[optionImageProperty].startsWith('www') ||
                          option[optionImageProperty].startsWith('/')
                        "
                        :src="option[optionImageProperty]"
                        alt=""
                        :style="optionImageStyle" />
                      <div
                        v-else
                        v-sanitize-html="option[optionImageProperty]"
                        class="svg-container"
                        :style="optionImageStyle" />
                    </div>
                    <span
                      class="option-label"
                      :title="option[optionLabelProperty]">
                      {{ option[optionLabelProperty] }}
                      <span
                        v-if="option.archived"
                        v-tooltip="capitalize($t('messages.locationHasBeenArchived'))"
                        class="archived">
                        {{ $t('states.archived').toUpperCase() }}
                      </span>
                      <span
                        v-if="showTooltipOnOptions && option[optionLabelDescription] != null"
                        v-tooltip.top="option[optionLabelDescription]"
                        class="label-tooltip">
                        <icon-info />
                      </span>
                    </span>
                  </label>
                  <a
                    class="option-action visible-on-hover mr-2"
                    :title="capitalize($t('actions.duplicate'))">
                    <span
                      v-if="allowDuplicate"
                      class="duplicate-action"
                      @click.stop.prevent="$emit('duplicateSelection', option)">
                      <icon-duplicate />
                    </span>
                  </a>
                  <a
                    v-if="allowExclusions && !isExcluded(option) && !isReadOnly"
                    class="option-action visible-on-hover mr-2"
                    data-action-type="exclude"
                    @click.stop.prevent="toggleExclusion(option)"
                    >{{ $t('actions.exclude') }}</a
                  >
                  <a
                    v-if="allowExclusions && isExcluded(option) && !isReadOnly"
                    class="option-action visible-on-hover mr-2"
                    data-action-type="include"
                    @click.stop.prevent="toggleExclusion(option)"
                    >{{ $t('actions.include') }}</a
                  >
                  <a
                    v-if="!isReadOnly && isMultiselect"
                    class="option-action visible-on-hover"
                    data-action-type="only"
                    @click.stop.prevent="selectOnly(option)"
                    >{{ $t('messages.only') }}</a
                  >
                </template>

                <!-- USE AUTOCOMPLETE -->
                <template v-else-if="isAutoComplete">
                  <div
                    class="flex-h"
                    style="width: 100%">
                    <div
                      :title="option[optionLabelProperty]"
                      style="flex: 1; font-size: 0.9em; word-break: break-word">
                      {{ option[optionLabelProperty] }}
                    </div>
                    <button
                      v-tooltip="capitalize($t('actions.include'))"
                      name="include-option"
                      class="btn btn-single-character btn-success ml-2"
                      style="flex-shrink: 0"
                      @click.stop.prevent="toggleOption(option)">
                      +
                    </button>
                    <button
                      v-if="allowExclusions"
                      v-tooltip="capitalize($t('actions.exclude'))"
                      name="exclude-option"
                      class="btn btn-single-character btn-danger ml-2"
                      style="flex-shrink: 0"
                      @click.stop.prevent="toggleExclusion(option)">
                      –
                    </button>
                  </div>
                </template>

                <!-- USE TOGGLES -->
                <template v-else>
                  <label class="flex-h">
                    <div
                      v-if="option[optionImageProperty]"
                      class="option-image">
                      <img
                        v-if="
                          option[optionImageProperty].startsWith('http') ||
                          option[optionImageProperty].startsWith('www') ||
                          option[optionImageProperty].startsWith('/')
                        "
                        :src="option[optionImageProperty]"
                        alt=""
                        :style="optionImageStyle" />
                      <div
                        v-else
                        v-sanitize-html="option[optionImageProperty]"
                        class="svg-container"
                        :style="optionImageStyle" />
                    </div>
                    <span
                      :title="option[optionLabelProperty]"
                      class="option-label"
                      >{{ option[optionLabelProperty] }}</span
                    >
                    <toggle-button
                      style="flex-grow: 0; margin: 0.4rem 0"
                      :color="colors.success"
                      :value="isSelected(option)"
                      :disabled="isReadOnly || option.isReadOnly"
                      @click.stop.prevent=""
                      @change="toggleOption(option)" />
                  </label>
                </template>
              </template>
              <template v-else>
                <label
                  class="flex-h"
                  :class="{
                    selected: internalSelectedOptions.some(
                      (x) => x[optionTrackByProperty] === option[optionTrackByProperty],
                    ),
                  }">
                  <div
                    v-if="option[optionImageProperty]"
                    class="option-image">
                    <img
                      v-if="
                        option[optionImageProperty].startsWith('http') ||
                        option[optionImageProperty].startsWith('www') ||
                        option[optionImageProperty].startsWith('/')
                      "
                      :src="option[optionImageProperty]"
                      alt=""
                      :style="optionImageStyle" />
                    <div
                      v-else
                      v-sanitize-html="option[optionImageProperty]"
                      class="svg-container"
                      :style="optionImageStyle" />
                  </div>
                  <span
                    class="option-label"
                    :title="option[optionLabelProperty]"
                    >{{ option[optionLabelProperty] }}
                    <span
                      v-if="showTooltipOnOptions && option[optionLabelDescription] != null"
                      v-tooltip.top="option[optionLabelDescription]"
                      class="label-tooltip">
                      <icon-info />
                    </span>
                  </span>
                </label>
              </template>
            </li>
            <!-- LOADER -->
            <div
              v-if="isLoadingMoreOptions"
              class="m-3">
              <loader-small />
              {{ capitalize($t('states.loading')) }}...
            </div>
            <li
              v-else-if="
                maxVisibleItems < filteredOptions.length ||
                (totalOptionsCount !== null && totalOptionsCount != this.options.length)
              ">
              <a @click.stop="handleLoadMoreOptions">+ {{ capitalize($t('messages.more')) }}</a>
            </li>
          </ul>
          <div
            v-if="totalOptionsCount != null && totalOptionsCount > 10 && filteredOptions.length > 0"
            class="total-options-count">
            {{ $t('messages.xResultsFound', [formatNumber(totalOptionsCount, '0,0')]) }}
          </div>
        </div>

        <!-- AUTOCOMPLETE MODE - SHOW INCLUSION AND EXCLUSIONS IN SEPARATE BLOCK -->
        <div
          v-if="isAutoComplete"
          class="drop-down-popup-selections">
          <template v-if="inclusions.length > 0">
            <div
              class="subsection"
              :class="isReadOnly ? 'subsection--read-only' : ''">
              <div class="subsection-header success">&checkmark; {{ capitalize($t('states.targeting')) }}</div>
              <div class="subsection-row">
                <div
                  v-if="inclusions.length > 1000"
                  class="ml-3 mr-3">
                  <i>{{ $t('messages.tooManyItemsToDisplay') }}</i>
                </div>
                <ul v-else>
                  <li
                    v-for="inclusion in inclusions"
                    :key="inclusion[optionLabelProperty]">
                    <span>{{ inclusion[optionLabelProperty] }}</span>
                    <button
                      v-if="!isReadOnly"
                      class="btn btn-secondary-hollow btn-single-character"
                      @click="unselectOption(inclusion)">
                      &times;
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <template v-if="exclusions.length > 0">
            <div class="subsection">
              <div class="subsection-header danger">&times; {{ capitalize($t('states.excluding')) }}</div>
              <div class="subsection-row">
                <div
                  v-if="exclusions.length > 1000"
                  class="ml-3 mr-3">
                  <i>{{ $t('messages.tooManyItemsToDisplay') }}</i>
                </div>
                <ul v-else>
                  <li
                    v-for="exclusion in exclusions"
                    :key="exclusion[optionLabelProperty]">
                    <span>{{ exclusion[optionLabelProperty] }}</span>
                    <button
                      v-if="!isReadOnly"
                      class="btn btn-secondary-hollow btn-single-character"
                      @click="unselectOption(exclusion)">
                      &times;
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </div>

      <slot name="drop-down-popup-bottom" />
      <div
        v-if="isReadOnly"
        class="drop-down-popup-footer">
        <span class="badge badge-rounded badge-warning">{{ capitalize($t('messages.thisFilterIsReadOnly')) }}</span>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import { capitalize } from 'vue';

  import Colors from '@/Colors';
  import CheckBox from '@/common/components/controls/CheckBox.vue';
  import { LoaderSmall } from '@/common/components/controls/index';
  import {
    IconArrowDown,
    IconClose,
    IconDuplicate,
    IconExcluded,
    IconInfo,
    IconLock,
    IconLocked,
  } from '@/common/icons/monochrome';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { areArraysEqual } from '@/globals';
  import formatNumber from '@/utils/NumberFormatter';
  import i18n from '@/vueI18n';

  const DefaultMaximumVisibleItems = 20;

  export default {
    components: {
      CheckBox,
      IconArrowDown,
      IconClose,
      IconDuplicate,
      IconExcluded,
      IconInfo,
      IconLock,
      IconLocked,
      LoaderSmall,
    },
    props: {
      id: {
        type: String,
        default: 'dropdown',
      },
      label: {
        type: String,
      },
      shortLabel: {
        type: String,
        default: null,
      },
      popupLabel: {
        type: String,
        default: null,
      },
      options: {
        type: Array,
      },
      totalOptionsCount: {
        type: Number,
        default: null,
      },
      showOptionsOnEmptySearch: {
        type: Boolean,
        default: false,
      },
      useGroups: {
        type: Boolean,
        default: false,
      },
      selectedOptions: {
        type: Array,
        default: null,
      },
      excludedOptions: {
        type: Array,
        default: null,
      },
      selectAllByDefault: {
        type: Boolean,
        default: true,
      },
      optionLabelProperty: {
        type: String,
        default: 'name',
      },
      optionLabelParentProperty: {
        type: String,
        default: 'name',
      },
      optionTrackByProperty: {
        type: String,
        default: 'id',
      },
      optionImageProperty: {
        type: String,
        default: 'image',
      },
      optionImageWidth: {
        type: String,
        default: null,
      },
      optionImageHeight: {
        type: String,
        default: null,
      },
      optionDisabledProperty: {
        type: String,
        default: null,
      },
      searchLabelProperties: {
        type: Array,
        default: null,
      },
      optionLabelDescription: {
        type: String,
        default: 'description',
      },
      returnEmptySelectedOptionsWhenAllIsSelected: {
        type: Boolean,
        default: true,
      },
      showArrow: {
        type: Boolean,
        default: true,
      },
      useCheckboxes: {
        type: Boolean,
        default: true,
      },
      showSummaryOfSelectionsInsteadOfLabel: {
        type: Boolean,
        default: true,
      },
      tintWhenSelections: {
        type: Boolean,
        default: true,
      },
      tintClass: {
        type: String,
        default: null,
      },
      showSelectAll: {
        type: Boolean,
        default: true,
      },
      showTooltipOnButton: {
        type: Boolean,
        default: false,
      },
      showTooltipOnOptions: {
        type: Boolean,
        default: false,
      },
      markAsHavingSelectionsWhenAllAreSelected: {
        type: Boolean,
        default: false,
      },
      showSelectionsInLabel: {
        type: Boolean,
        default: true,
      },
      allowExclusions: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: [Object, Function],
        default: null,
      },
      useAlternateStyle: {
        type: Boolean,
        default: false,
      },
      useTableFilterStyle: {
        type: Boolean,
        default: false,
      },
      alignDropDownToRight: {
        type: Boolean,
        default: false,
      },
      showClear: {
        type: Boolean,
        default: true,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      showFloatingLabel: {
        type: Boolean,
        default: false,
      },
      isMandatory: {
        type: Boolean,
        default: false,
      },
      showFloatingLabelWhenOnlyOneOptionIsAvailable: {
        type: Boolean,
        default: false,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isLoadingMoreOptions: {
        type: Boolean,
        default: false,
      },
      isAutoComplete: {
        type: Boolean,
        default: false,
      },
      canToggleGroups: {
        type: Boolean,
        default: false,
      },
      enableSearch: {
        type: Boolean,
        default: false,
      },
      searchPlaceholder: {
        type: String,
        default: () => i18n.global.t('actions.search'),
      },
      clearInternalOptionsOnReadOnly: {
        type: Boolean,
        default: false,
      },
      showPopupHeader: {
        type: Boolean,
        default: true,
      },
      useCompactView: {
        type: Boolean,
        default: false,
      },
      showLabelBeforeSelections: {
        type: Boolean,
        default: false,
      },
      isMultiselect: {
        type: Boolean,
        default: true,
      },
      dropDownContainerWidth: {
        type: String,
        default: '34rem',
      },
      dropDownContainerMinWidth: {
        type: String,
        default: '0',
      },
      radiusLeft: {
        type: String,
        default: '2rem',
      },
      radiusRight: {
        type: String,
        default: '2rem',
      },
      shouldAlwaysDisplayAllSelectionsInLabel: {
        type: Boolean,
        default: false,
      },
      labelPlaceholder: {
        type: String,
        default: null,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      useMultiselectStyle: {
        type: Boolean,
        default: false,
      },
      enableParentSearch: {
        type: Boolean,
        default: false,
      },
      popupPositionFixed: {
        type: Boolean,
        default: false,
      },
      showCloseButton: {
        type: Boolean,
        default: true,
      },
      allowGrandChildren: {
        type: Boolean,
        default: true,
      },
      useImage: {
        type: Boolean,
        default: true,
      },
      allowDuplicate: {
        type: Boolean,
        default: false,
      },
      iconStyle: {
        type: String,
        default: null,
      },
      useDefaultOption: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        colors: Colors,
        internalExcludedOptions: [],
        internalSelectedOptions: [],
        isDropDownPopupVisible: false,
        isInitialized: false,
        isReady: false,
        maxVisibleItems: DefaultMaximumVisibleItems,
        openUpwards: false,
        optionFocusIndex: 0,
        previouslySelectedOptions: [],
        searchTerm: null,
        shouldSelectAll: true,
        DefaultMaximumVisibleItems,
        hasBeenToggled: false,
      };
    },
    computed: {
      hasSelection() {
        //
        // When only one option is available floating label doesn't show up when one item is selected.
        // To make it work, added this property showFloatingLabelWhenOnlyOneOptionIsAvailable.
        //
        if (this.showFloatingLabelWhenOnlyOneOptionIsAvailable) {
          return (
            this.internalSelectedOptions != null &&
            this.options != null &&
            this.internalSelectedOptions.length > 0 &&
            (this.inclusions.length > 0 || this.exclusions.length > 0)
          );
        }

        if (this.isAutoComplete) {
          return this.inclusions.length > 0 || this.exclusions.length > 0;
        }

        return (
          this.internalSelectedOptions != null &&
          this.options != null &&
          this.internalSelectedOptions.length > 0 &&
          (this.inclusions.length < this.allOptions.length || this.markAsHavingSelectionsWhenAllAreSelected) &&
          this.exclusions.length < this.allOptions.length
        );
      },
      inclusions() {
        return this.internalSelectedOptions.filter((x) => !this.isExcluded(x));
      },
      exclusions() {
        return this.internalSelectedOptions.filter((x) => this.isExcluded(x));
      },
      hasInclusions() {
        return this.hasSelection && this.inclusions.length > 0;
      },
      hasExclusions() {
        return this.hasSelection && this.exclusions.length > 0;
      },
      internalUnselectedOptions() {
        return this.allOptions.filter(
          (x) =>
            this.internalSelectedOptions.find((y) => x[this.optionTrackByProperty] === y[this.optionTrackByProperty]) ==
            null,
        );
      },
      labelOrValue() {
        if (!this.hasSelection || !this.showSummaryOfSelectionsInsteadOfLabel || !this.showSelectionsInLabel) {
          return this.labelPlaceholder || this.label;
        }
        if (this.shouldAlwaysDisplayAllSelectionsInLabel) {
          return this.internalSelectedOptions.map((x) => x[this.optionLabelProperty]).join(', ');
        }
        const additional =
          this.internalSelectedOptions.length > 1 ? ` +${this.internalSelectedOptions.length - 1}` : '';
        let name = this.internalSelectedOptions[0][this.optionLabelProperty];
        if (name.length > 20) {
          name = `${name.slice(0, 17)}...`;
        }
        if (this.isExcluded(this.internalSelectedOptions[0])) {
          name = `<span style="text-decoration: line-through;">${name}</span>`;
        }
        const prefix = this.showLabelBeforeSelections ? `${this.label}: ` : '';
        return `${prefix}${name}${additional}`;
      },
      value() {
        if (!this.hasSelection) {
          return null;
        }
        const additional =
          this.internalSelectedOptions.length > 1 ? ` +${this.internalSelectedOptions.length - 1}` : '';
        const name = this.internalSelectedOptions[0][this.optionLabelProperty];
        return `${name}${additional}`;
      },
      filteredOptions() {
        // Optimization - If the dropdown has not been toggled yet, don't compute any options
        if (!this.hasBeenToggled) {
          return [];
        }
        if (
          this.options == null ||
          (this.isAutoComplete && (this.searchTerm == null || this.searchTerm.trim() === ''))
        ) {
          return this.showOptionsOnEmptySearch ? this.options : [];
        }
        const groupSearch = (x) =>
          this.isSearchByParentProperty(x)
            ? x.children
            : x.children.some((c) => this.matchesSearchTerm(c[this.optionLabelProperty], c));
        const searchResults = () =>
          this.options.filter((x) =>
            this.useGroups ? groupSearch(x) : this.matchesSearchTerm(x[this.optionLabelProperty], x),
          );

        let options =
          this.searchTerm == null || this.searchTerm.trim() === '' ? [...this.options] : [...searchResults()];
        options = this.isAutoComplete ? options.filter((x) => !this.isSelected(x)) : options;

        if (this.allOptions.length <= 5) {
          return options;
        }

        const sortFn = (a, b) => `${a[this.optionLabelProperty]}`.localeCompare(`${b[this.optionLabelProperty]}`);

        // Separates the options array into two arrays: the previously selected and unselected. Done in a reduce for efficiency
        const { previouslySelected, previouslyUnselected } = options.reduce(
          (output, option) => {
            if (this.wasPreviouslySelected(option)) {
              output.previouslySelected.push(option);
            } else {
              output.previouslyUnselected.push(option);
            }
            return output;
          },
          {
            previouslySelected: [],
            previouslyUnselected: [],
          },
        );

        options = [...previouslySelected.sort(sortFn), ...previouslyUnselected.sort(sortFn)];
        return options;
      },
      visibleFilteredOptions() {
        return this.filteredOptions.slice(0, this.maxVisibleItems);
      },
      allOptions() {
        if (this.useGroups && this.options != null) {
          return [...this.options.reduce((mem, x) => [...mem, ...x.children], [])];
        }
        if (this.options?.length > 0) {
          return [...this.options];
        }
        return [];
      },
      buttonClasses() {
        return [
          ...(this.hasSelection && this.tintWhenSelections ? ['btn-secondary-hollow'] : []),
          ...(!this.tintWhenSelections || !this.hasSelection ? ['btn-default'] : []),
          ...(this.tintWhenSelections && this.hasInclusions && !this.hasExclusions ? ['tinted-success'] : []),
          ...(this.tintWhenSelections && this.hasInclusions && this.hasExclusions ? ['tinted-warning'] : []),
          ...(this.tintWhenSelections && !this.hasInclusions && this.hasExclusions ? ['tinted-error'] : []),
          ...(this.useCompactView ? ['btn-xs'] : []),
          ...(!this.tintClass && this.tintWhenSelections && this.hasInclusions && !this.hasExclusions
            ? ['tinted-success']
            : []),
          ...(!this.tintClass && this.tintWhenSelections && this.hasInclusions && this.hasExclusions
            ? ['tinted-warning']
            : []),
          ...(!this.tintClass && this.tintWhenSelections && !this.hasInclusions && this.hasExclusions
            ? ['tinted-error']
            : []),
          ...(this.tintClass != null ? [`tinted-${this.tintClass}`] : []),
        ].join(' ');
      },
      buttonTooltipContent() {
        if (!this.showTooltipOnButton) return null;
        let content = '';
        if (this.hasInclusions) {
          content = `${content}<div>${capitalize(i18n.global.t('states.included'))}:`;
          const included = this.inclusions.map((x) => `<li>${x[this.optionLabelProperty]}</li>`).join('');
          content = `${content}<ul style="padding-left: 1.6rem; font-weight: 400;">${included}</ul>`;
        }
        if (this.hasExclusions) {
          content = `${content}<div>${capitalize(i18n.global.t('states.excluded'))}:`;
          const excluded = this.exclusions.map((x) => `<li>${x[this.optionLabelProperty]}</li>`).join('');
          content = `${content}<ul style="padding-left: 1.6rem; font-weight: 400;">${excluded}</ul>`;
        }
        return content;
      },
      optionImageStyle() {
        let style = '';
        if (this.optionImageWidth) style = `${style}width: ${this.optionImageWidth};`;
        if (this.optionImageHeight) style = `${style}width: ${this.optionImageHeight};`;
        return style;
      },
    },
    watch: {
      options() {
        this.initSelections();
      },
      isDisabled(val) {
        if (val && this.isDropDownPopupVisible) {
          this.closeDropDown();
        }
      },
      internalSelectedOptions: {
        deep: true,
        handler() {
          if (!this.isReady || !this.isInitialized) {
            return;
          }
          if (this.selectionChangedTimer) {
            clearTimeout(this.selectionChangedTimer);
          }
          this.selectionChangedTimer = setTimeout(() => {
            this.selectionChangedTimer = null;
            const included = this.internalSelectedOptions.filter((x) => !this.isExcluded(x));
            const excluded = this.internalSelectedOptions.filter((x) => this.isExcluded(x));
            const returnEmptySelections =
              !this.isAutoComplete &&
              this.returnEmptySelectedOptionsWhenAllIsSelected &&
              included.length === this.allOptions.length;

            this.$emit(
              'selectionChanged',
              returnEmptySelections
                ? {
                    included: [],
                    excluded: [],
                  }
                : {
                    included,
                    excluded,
                  },
            );
          }, 100);

          // we are just selecting one option, once that option is clicked, there is no need to keep the dropdown open
          if (this.useGroups && !this.useCheckboxes) {
            this.closeDropDown();
          }
        },
      },
      isDropDownPopupVisible(val) {
        if (val && this.$refs.searchInput != null) {
          this.searchTerm = null;
          this.$refs.searchInput.focus();
        }
        this.maxVisibleItems = DefaultMaximumVisibleItems;
      },
      selectedOptions(val) {
        if (areArraysEqual(val, this.internalSelectedOptions)) {
          return;
        }
        this.shouldSelectAll = this.selectAllByDefault;
        this.initSelections();
      },
      selectAllByDefault(val) {
        this.handleSelectAllToggleButtonChange(val);
      },
      searchTerm() {
        if (this.searchTermTimer) {
          clearTimeout(this.searchTermTimer);
        }
        this.searchTermTimer = setTimeout(() => {
          this.searchTermTimer = null;
          this.$emit('searchTermChanged', this.searchTerm);
        }, 200);
      },
      isReadOnly() {
        if (this.isReady && this.clearInternalOptionsOnReadOnly) {
          this.internalSelectedOptions = [];
          this.internalExcludedOptions = [];
        }
      },
    },
    beforeMount() {
      this.shouldSelectAll = this.selectAllByDefault;
    },
    mounted() {
      this.initSelections();
      this.isInitialized = true;
      window.addEventListener('resize', this.updateOpeningDirection);
      window.addEventListener('click', (event) => this.handleClickOutside(event));
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.updateOpeningDirection);
      window.removeEventListener('click', (event) => this.handleClickOutside(event));
    },
    methods: {
      capitalize,
      formatNumber,
      handleSelectAllToggleButtonChange(val) {
        if (this.isReadOnly) {
          return;
        }
        this.internalExcludedOptions = []; // Always clear exclusions.
        if (val) {
          if (this.isAutoComplete) {
            this.internalSelectedOptions = [];
          } else {
            const options = [...this.allOptions];
            this.internalSelectedOptions = options;
          }
        } else {
          // We shouldn't clear locked/disabled options when clearing all.
          this.internalSelectedOptions = this.internalSelectedOptions.filter((x) => x[this.optionDisabledProperty]);
        }
        this.shouldSelectAll = val;
      },
      isSelected(option) {
        return (
          ((option.children == null || option.children.length === 0 || !this.allowGrandChildren) &&
            this.internalSelectedOptions.some(
              (x) => x[this.optionTrackByProperty] === option[this.optionTrackByProperty],
            )) ||
          (this.useGroups &&
            option.children != null &&
            option.children.length > 0 &&
            !option.children.some((x) => !this.isSelected(x)))
        );
      },
      isExcluded(option) {
        return (
          ((option.children == null || option.children.length === 0 || !this.allowGrandChildren) &&
            this.internalExcludedOptions.some(
              (x) => x[this.optionTrackByProperty] === option[this.optionTrackByProperty],
            )) ||
          (this.useGroups &&
            option.children != null &&
            option.children.length > 0 &&
            !option.children.some((x) => !this.isExcluded(x)))
        );
      },
      /**
       * Checks whether the option was previously selected (included in the `previouslySelectedOptions` array).
       * Performs the check by comparing the `optionTrackByProperty`.
       * @param option
       * @returns {boolean}
       */
      wasPreviouslySelected(option) {
        return (
          ((option.children == null || option.children.length === 0) &&
            this.previouslySelectedOptions.some(
              (x) => x[this.optionTrackByProperty] === option[this.optionTrackByProperty],
            )) ||
          (this.useGroups &&
            option.children != null &&
            option.children.length > 0 &&
            !option.children.some((x) => !this.wasPreviouslySelected(x)))
        );
      },
      toggleOption(option) {
        GlobalEventBus.$emit(GlobalEvents.clearTooltips);

        if (this.isReadOnly) {
          return;
        }
        //
        // Parent or Grandparent of a group
        //
        if (
          (this.allowGrandChildren && this.isParentOption(option)) ||
          (!this.allowGrandChildren && this.isGrandParentOption(option))
        ) {
          if (this.isSelected(option)) {
            this.internalExcludedOptions = this.internalExcludedOptions.filter(
              (x) => !option.children.some((c) => c[this.optionTrackByProperty] === x[this.optionTrackByProperty]),
            );
            this.internalSelectedOptions = this.internalSelectedOptions.filter(
              (x) => !option.children.some((c) => c[this.optionTrackByProperty] === x[this.optionTrackByProperty]),
            );
          } else {
            this.internalExcludedOptions = this.internalExcludedOptions.filter(
              (x) => !option.children.some((c) => c[this.optionTrackByProperty] === x[this.optionTrackByProperty]),
            );
            this.internalSelectedOptions.push(
              ...option.children.filter(
                (x) =>
                  !this.internalSelectedOptions.some(
                    (o) => o[this.optionTrackByProperty] === x[this.optionTrackByProperty],
                  ),
              ),
            );
          }
        } else {
          // Regular option
          if (this.isSelected(option)) {
            this.internalExcludedOptions = this.internalExcludedOptions.filter(
              (x) => x[this.optionTrackByProperty] !== option[this.optionTrackByProperty],
            );
            this.internalSelectedOptions = this.internalSelectedOptions.filter(
              (x) => x[this.optionTrackByProperty] !== option[this.optionTrackByProperty],
            );
          } else {
            this.internalSelectedOptions = [...this.internalSelectedOptions, option];
          }
        }
        this.$emit('toggleOptionChanged', option, this.isSelected(option));
        this.shouldSelectAll = this.internalSelectedOptions.length === this.allOptions.length;
      },
      toggleExclusion(option) {
        GlobalEventBus.$emit(GlobalEvents.clearTooltips);

        if (this.isReadOnly) {
          return;
        }

        // This logic should not happen in autocomplete mode because all the options may not be loaded due to search filters
        if (!this.isAutoComplete && this.internalSelectedOptions.length === this.allOptions.length) {
          this.internalSelectedOptions = [];
        }

        //
        // Parent or Grandparent of a group
        //
        if (
          (this.allowGrandChildren && this.isParentOption(option)) ||
          (!this.allowGrandChildren && this.isGrandParentOption(option))
        ) {
          if (this.isExcluded(option)) {
            this.internalExcludedOptions = this.internalExcludedOptions.filter(
              (x) => !option.children.some((c) => c[this.optionTrackByProperty] === x[this.optionTrackByProperty]),
            );
          } else {
            this.internalExcludedOptions.push(
              ...option.children.filter(
                (x) =>
                  !this.internalExcludedOptions.some(
                    (o) => o[this.optionTrackByProperty] === x[this.optionTrackByProperty],
                  ),
              ),
            );
          }
          if (this.isSelected(option)) {
            this.internalSelectedOptions = this.internalSelectedOptions.filter(
              (x) => x[this.optionTrackByProperty] !== option[this.optionTrackByProperty],
            );
          } else {
            this.internalSelectedOptions.push(
              ...option.children.filter(
                (x) =>
                  !this.internalSelectedOptions.some(
                    (o) => o[this.optionTrackByProperty] === x[this.optionTrackByProperty],
                  ),
              ),
            );
          }
        } else {
          // Regular option
          if (this.isExcluded(option)) {
            this.internalExcludedOptions = this.internalExcludedOptions.filter(
              (x) => x[this.optionTrackByProperty] !== option[this.optionTrackByProperty],
            );
          } else {
            this.internalExcludedOptions.push(option);
          }
          if (!this.isSelected(option)) {
            this.internalSelectedOptions.push(option);
          } else if (this.internalSelectedOptions.length === this.allOptions.length) {
            this.selectOnly(option, true);
          } else {
            // Just to trigger a change in selections.
            this.internalSelectedOptions = [...this.internalSelectedOptions];
          }
        }
        this.shouldSelectAll = this.internalSelectedOptions.length === this.allOptions.length;
      },
      selectOption(option) {
        if (this.isReadOnly) {
          return;
        }

        this.internalSelectedOptions = [option];
      },
      unselectOption(option) {
        if (this.isReadOnly) {
          return;
        }

        this.internalExcludedOptions = this.internalExcludedOptions.filter((o) => o !== option);
        this.internalSelectedOptions = this.internalSelectedOptions.filter((o) => o !== option);
      },
      selectOnly(option, asExclusion = false) {
        if (this.isReadOnly) {
          return;
        }

        //
        // Parent or Grandparent of a group
        //
        if (
          (this.allowGrandChildren && this.isParentOption(option)) ||
          (!this.allowGrandChildren && this.isGrandParentOption(option))
        ) {
          this.internalSelectedOptions = [...option.children];
          if (asExclusion) {
            this.internalExcludedOptions = [...option.children];
          } else {
            this.internalExcludedOptions = [];
          }
        } else {
          // Regular option
          // If any disabled options are selected, we should not deselect them.
          const selectedDisabledOptions = this.internalSelectedOptions.filter((x) => x[this.optionDisabledProperty]);
          this.internalSelectedOptions = [...selectedDisabledOptions, option];
          if (asExclusion) {
            this.internalExcludedOptions = [option];
          } else {
            this.internalExcludedOptions = [];
          }
        }
        this.shouldSelectAll = this.internalSelectedOptions.length === this.allOptions.length;
      },
      clear() {
        if (this.isReadOnly) {
          return;
        }

        this.handleSelectAllToggleButtonChange(this.selectAllByDefault);
        this.$emit('cleared');
      },
      initSelections(forceAutocomplete = false, includedOptions = null, excludedOptions = null) {
        if (this.isAutoComplete) {
          if (forceAutocomplete) {
            this.internalSelectedOptions =
              excludedOptions != null ? [...includedOptions, ...excludedOptions] : [...includedOptions];
            if (excludedOptions) {
              this.internalExcludedOptions = excludedOptions;
            }
          }
          this.isReady = true;
          return;
        }
        this.isReady = false;
        let selectedOptions = [];
        let defaultOptions = [];
        if (this.options != null) {
          defaultOptions = this.useDefaultOption ? this.options.filter((o) => o.isDefault) : [];
          selectedOptions = this.selectAllByDefault || this.shouldSelectAll ? [...this.allOptions] : [];
        }
        if (this.selectedOptions != null) {
          if (!(this.selectedOptions.length === 0 && this.shouldSelectAll)) {
            selectedOptions = [
              ...this.allOptions.filter((x) =>
                this.selectedOptions
                  .filter((y) => y != null) // Badly serialized filters could cause an issue if not filtered out.
                  .find((y) => y[this.optionTrackByProperty] === x[this.optionTrackByProperty]),
              ),
            ];
            if (this.allOptions.length > 0) {
              this.shouldSelectAll = selectedOptions.length === this.allOptions.length;
            }
          } else if (this.selectedOptions.length === 0 && !this.selectAllByDefault) {
            this.internalSelectedOptions = [];
            this.shouldSelectAll = this.selectedOptions.length === this.allOptions.length;
          }
        }
        if (this.excludedOptions != null && this.excludedOptions.length > 0) {
          this.internalExcludedOptions = this.excludedOptions;
        }
        if (!this.selectedOptions?.length && defaultOptions?.length) {
          selectedOptions = defaultOptions;
          this.isReady = true;
        }
        this.internalSelectedOptions = selectedOptions;
        setTimeout(() => {
          this.isReady = true;
        }, 100);
      },
      updateOpeningDirection() {
        const bottom = (this.$refs.popupContainer?.getBoundingClientRect().top || 0) + 250; // 25rem
        this.openUpwards = bottom > window.innerHeight;
      },
      showDropDown() {
        this.updateOpeningDirection();
        this.isDropDownPopupVisible = true;
        if (!this.hasBeenToggled) {
          this.hasBeenToggled = true;
        }
        this.$emit('dropDownVisible');
      },
      closeDropDown() {
        this.openUpwards = false;
        this.isDropDownPopupVisible = false;
        // Optimization - Only update the previously selected options if the selected options have changed, otherwise this triggers the computation of the `filteredOption` computed property which is expensive
        if (
          (this.selectedOptions?.length > 0 && this.previouslySelectedOptions.length !== this.selectedOptions.length) ||
          this.previouslySelectedOptions.some((option) => this.selectedOptions.indexOf(option) === -1)
        ) {
          this.previouslySelectedOptions = [...this.selectedOptions];
        }
        this.$emit('dropDownHidden');
      },
      toggleDropDown() {
        this.optionFocusIndex = -1;
        GlobalEventBus.$emit(GlobalEvents.clearTooltips);
        setTimeout(() => GlobalEventBus.$emit(GlobalEvents.clearTooltips), 500);
        if (this.isDropDownPopupVisible) {
          this.closeDropDown();
        } else {
          this.showDropDown();
        }
      },
      handleLoadMoreOptions() {
        if (this.totalOptionsCount != this.options.length) {
          this.$emit('loadMoreOptions');
        }
        this.maxVisibleItems += DefaultMaximumVisibleItems;
      },
      matchesSearchTerm(property, options) {
        const isSearchMatch = (x) =>
          this.searchTerm == null ||
          this.searchTerm.trim() === '' ||
          x.toLowerCase().includes(this.searchTerm.toLowerCase().trim()) ||
          x
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .includes(this.searchTerm.toLowerCase().trim());

        if (this.searchLabelProperties?.length > 0) {
          return this.searchLabelProperties.map((i) => isSearchMatch(options[i])).includes(true);
        }
        return isSearchMatch(property);
      },
      isParentOption(option) {
        // Had to add the option to toggle isGrandChild because some venues on the grandparent level are technically not grandparents
        return (
          (option.children != null && option.children.length > 0 && this.useGroups) ||
          (!this.allowGrandChildren && !option.isGrandChild)
        );
      },
      isGrandParentOption(option) {
        let isGrandparent = false;

        option.children.forEach((child) => {
          if (this.isParentOption(child)) isGrandparent = true;
        });

        return isGrandparent;
      },
      onUpKey() {
        const checkboxes = this.$refs.popupContainer.querySelectorAll('input[type=checkbox]');
        this.optionFocusIndex = Math.max(this.optionFocusIndex - 1, -1);
        if (this.optionFocusIndex > -1) {
          const cb = checkboxes[this.optionFocusIndex];
          if (cb != null) {
            cb.focus();
          }
        } else {
          this.$refs.mainButton.focus();
        }
      },
      onDownKey() {
        const checkboxes = this.$refs.popupContainer.querySelectorAll('input[type=checkbox]');
        this.optionFocusIndex = Math.min(this.optionFocusIndex + 1, checkboxes.length - 1);
        const cb = checkboxes[this.optionFocusIndex];
        if (cb != null) {
          cb.focus();
        }
      },
      handleClickOutside(event) {
        const thisDropdown = this.$refs.dropdownContainer;
        if (this.isDropDownPopupVisible && !thisDropdown.contains(event.target)) {
          this.closeDropDown();
        }
      },
      isSearchByParentProperty(group) {
        return (
          this.useGroups && this.enableParentSearch && this.matchesSearchTerm(group[this.optionLabelParentProperty])
        );
      },
      filteredGroupResult(group) {
        return this.isSearchByParentProperty(group)
          ? group.children
          : group.children.filter((x) => this.matchesSearchTerm(x[this.optionLabelProperty], x));
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use 'styles';

  #app .btn-drop-down.tinted-hollow-success {
    background: var(--defaultButtonColorReverse);
    color: var(--defaultButtonColor);
    border-color: var(--success);

    &:hover {
      background: var(--success) !important;
      color: var(--successReverse) !important;

      svg {
        fill: var(--successReverse) !important;
        stroke: var(--successReverse) !important;
      }
    }

    svg {
      fill: var(--defaultButtonColor);
      stroke: var(--defaultButtonColor);
    }
  }
</style>
